import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import TrailingSlashRedirect from "@/components/TrailingSlashRedirect";
import RouteAnalytics from "@/components/RouteAnalytics";
import ChatLauncher from "@/components/ChatLauncher";
import LegacyRedirect from "@/components/LegacyRedirect";
import { initDeferredThirdParty } from "@/lib/thirdParty";

initDeferredThirdParty();

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const AdminArea = lazy(() => import("@/pages/admin/AdminArea"));
const AustinNewConstructionHomes = lazy(() => import("@/pages/AustinNewConstructionHomes"));
const CityHub = lazy(() => import("@/pages/CityHub"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const BuilderHub = lazy(() => import("@/pages/BuilderHub"));
const BuilderPage = lazy(() => import("@/pages/BuilderPage"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AreaHubPage = lazy(() => import("@/pages/AreaHubPage"));
const GuidesHub = lazy(() => import("@/pages/GuidesHub"));
const BuilderFinancingGuide = lazy(() => import("@/pages/BuilderFinancingGuide"));
const InventoryVsToBeBuiltGuide = lazy(() => import("@/pages/InventoryVsToBeBuiltGuide"));
const BuilderNegotiationGuide = lazy(() => import("@/pages/BuilderNegotiationGuide"));
const AustinBuilderIncentives = lazy(() => import("@/pages/AustinBuilderIncentives"));
const NewConstructionInspections = lazy(() => import("@/pages/NewConstructionInspections"));
const InspectionArticlePage = lazy(() => import("@/pages/InspectionArticlePage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const BuyerConsultation = lazy(() => import("@/pages/BuyerConsultation"));
const IABSPage = lazy(() => import("@/pages/IABSPage"));
const ConsumerProtectionNoticePage = lazy(() => import("@/pages/ConsumerProtectionNoticePage"));
const HomeValuation = lazy(() => import("@/pages/HomeValuation"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const StartPage = lazy(() => import("@/pages/StartPage"));
const WebsitesLanding = lazy(() => import("@/pages/WebsitesLanding"));
const BlueprintStart = lazy(() => import("@/pages/BlueprintStart"));
const CommunitiesHub = lazy(() => import("@/pages/CommunitiesHub"));
const CommunityPage = lazy(() => import("@/pages/CommunityPage"));
const NewConstructionUnder600k = lazy(() => import("@/pages/NewConstructionUnder600k"));
const IncentivesGuideLanding = lazy(() => import("@/pages/IncentivesGuideLanding"));
const QuickMoveInHomes = lazy(() => import("@/pages/QuickMoveInHomes"));
const NewConstructionInspectionChecklist = lazy(() => import("@/pages/NewConstructionInspectionChecklist"));
const MoveUpBuyersAustin = lazy(() => import("@/pages/MoveUpBuyersAustin"));
const RelocatingToAustinNewConstruction = lazy(() => import("@/pages/RelocatingToAustinNewConstruction"));
const OakHillParkwayDrippingSprings = lazy(() => import("@/pages/OakHillParkwayDrippingSprings"));
const ThankYouPage = lazy(() => import("@/pages/ThankYouPage"));
const CoverCueSupport = lazy(() => import("@/pages/CoverCueSupport"));
const CoverCuePrivacy = lazy(() => import("@/pages/CoverCuePrivacy"));
const SignRouteTerms = lazy(() => import("@/pages/SignRouteTerms"));
const SignRoutePrivacy = lazy(() => import("@/pages/SignRoutePrivacy"));
const SignRouteSupport = lazy(() => import("@/pages/SignRouteSupport"));
const BuildInAustin = lazy(() => import("@/pages/build/BuildInAustin"));
const BuildOnYourLotAustin = lazy(() => import("@/pages/build/BuildOnYourLotAustin"));
const AustinHomeInitiative = lazy(() => import("@/pages/build/AustinHomeInitiative"));
const AustinCustomHomeBuilders = lazy(() => import("@/pages/build/AustinCustomHomeBuilders"));
const PropertyPotentialReview = lazy(() => import("@/pages/build/PropertyPotentialReview"));
const AustinInfillRedevelopment = lazy(() => import("@/pages/build/AustinInfillRedevelopment"));
const RemodelOrRebuildAustin = lazy(() => import("@/pages/build/RemodelOrRebuildAustin"));
const SellYourLotToABuilder = lazy(() => import("@/pages/build/SellYourLotToABuilder"));

const LennarAustin = lazy(() => import("@/pages/builders/LennarAustin"));
const DRHortonVsLennarAustin = lazy(() => import("@/pages/builders/DRHortonVsLennarAustin"));
const DRHortonVsPerryHomesAustin = lazy(() => import("@/pages/builders/DRHortonVsPerryHomesAustin"));
const DRHortonIncentivesAustin2026 = lazy(() => import("@/pages/builders/DRHortonIncentivesAustin2026"));
const LennarVsPerryHomesAustin = lazy(() => import("@/pages/builders/LennarVsPerryHomesAustin"));
const PulteVsTaylorMorrisonAustin = lazy(() => import("@/pages/builders/PulteVsTaylorMorrisonAustin"));
const LennarVsPulteAustin = lazy(() => import("@/pages/builders/LennarVsPulteAustin"));

const queryClient = new QueryClient();
const cities = ["austin", "round-rock", "cedar-park", "leander", "georgetown", "pflugerville", "dripping-springs", "buda", "kyle", "lakeway"];
const builders = ["dr-horton", "perry-homes", "taylor-morrison", "pulte", "highland-homes", "meritage-homes", "drb-homes", "tri-pointe-homes", "trophy-signature-homes", "coventry-homes", "ashton-woods"];

const oldBuilderRoutes: Record<string, string> = {
  "/dr-horton-homes-austin/": "dr-horton",
  "/pulte-homes-austin/": "pulte",
  "/perry-homes-austin/": "perry-homes",
  "/taylor-morrison-homes-austin/": "taylor-morrison",
  "/highland-homes-austin/": "highland-homes",
  "/meritage-homes-austin/": "meritage-homes",
  "/builders/dr-horton-austin/": "dr-horton",
  "/builders/lennar-austin/": "lennar",
  "/builders/perry-homes-austin/": "perry-homes",
  "/builders/taylor-morrison-austin/": "taylor-morrison",
  "/builders/pulte-austin/": "pulte",
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <TrailingSlashRedirect />
        <RouteAnalytics />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/login/*" element={<AdminArea section="login" />} />
            <Route path="/crm/*" element={<AdminArea section="crm" />} />
            <Route path="/admin/*" element={<AdminArea section="admin" />} />
            <Route path="/" element={<Home />} />
            <Route path="/about/" element={<About />} />
            <Route path="/buyer-consultation/" element={<BuyerConsultation />} />

            <Route path="/new-construction/" element={<CityHub />} />
            <Route path="/new-construction/:citySlug/" element={<CityPage />} />
            {cities.map((city) => <Route key={`old-city-${city}`} path={`/new-construction-homes-${city}-tx/`} element={<LegacyRedirect to={`/new-construction/${city}/`} />} />)}
            <Route path="/new-construction-cities/" element={<LegacyRedirect to="/new-construction/" />} />
            <Route path="/austin-new-construction-homes/" element={<LegacyRedirect to="/new-construction/" />} />

            <Route path="/builders/" element={<BuilderHub />} />
            <Route path="/builders/lennar/" element={<LennarAustin />} />
            {builders.map((builder) => <Route key={builder} path={`/builders/${builder}/`} element={<BuilderPage builderSlugProp={builder} />} />)}
            {Object.entries(oldBuilderRoutes).map(([path, builder]) => <Route key={path} path={path} element={<LegacyRedirect to={`/builders/${builder}/`} />} />)}
            <Route path="/austin-home-builders/" element={<LegacyRedirect to="/builders/" />} />
            <Route path="/builders/dr-horton-vs-lennar-austin/" element={<DRHortonVsLennarAustin />} />
            <Route path="/builders/dr-horton-vs-perry-homes-austin/" element={<DRHortonVsPerryHomesAustin />} />
            <Route path="/builders/dr-horton-incentives-austin-2026/" element={<DRHortonIncentivesAustin2026 />} />
            <Route path="/builders/lennar-vs-perry-homes-austin/" element={<LennarVsPerryHomesAustin />} />
            <Route path="/builders/pulte-vs-taylor-morrison-austin/" element={<PulteVsTaylorMorrisonAustin />} />
            <Route path="/builders/lennar-vs-pulte-austin/" element={<LennarVsPulteAustin />} />

            <Route path="/guides/" element={<GuidesHub />} />
            <Route path="/guides/builder-incentives/" element={<AustinBuilderIncentives />} />
            <Route path="/guides/builder-financing/" element={<BuilderFinancingGuide />} />
            <Route path="/guides/new-construction-negotiation/" element={<BuilderNegotiationGuide />} />
            <Route path="/guides/inventory-vs-to-be-built/" element={<InventoryVsToBeBuiltGuide />} />
            <Route path="/guides/new-construction-inspections/" element={<NewConstructionInspections />} />
            <Route path="/austin-builder-incentives/" element={<LegacyRedirect to="/guides/builder-incentives/" />} />
            <Route path="/builder-negotiation-guide/" element={<LegacyRedirect to="/guides/new-construction-negotiation/" />} />
            <Route path="/new-construction-inspections/" element={<LegacyRedirect to="/guides/new-construction-inspections/" />} />
            <Route path="/new-construction-inspections/:articleSlug/" element={<InspectionArticlePage />} />

            <Route path="/blog/" element={<BlogIndex />} />
            <Route path="/blog/guides/builder-incentives/-july-2026/" element={<LegacyRedirect to="/blog/austin-builder-incentives-july-2026/" />} />
            <Route path="/blog/:blogSlug/" element={<BlogPost />} />
            <Route path="/north-austin-new-construction/" element={<AreaHubPage />} />
            <Route path="/south-austin-new-construction/" element={<AreaHubPage />} />
            <Route path="/west-austin-new-construction/" element={<AreaHubPage />} />
            <Route path="/central-austin-new-construction/" element={<AreaHubPage />} />
            <Route path="/iabs/" element={<IABSPage />} />
            <Route path="/consumer-protection-notice/" element={<ConsumerProtectionNoticePage />} />
            <Route path="/home-valuation/" element={<HomeValuation />} />
            <Route path="/privacy-policy/" element={<PrivacyPolicy />} />
            <Route path="/terms/" element={<TermsAndConditions />} />
            <Route path="/start/" element={<StartPage />} />
            <Route path="/websites/" element={<WebsitesLanding />} />
            <Route path="/blueprint-start/" element={<BlueprintStart />} />
            <Route path="/communities/" element={<CommunitiesHub />} />
            <Route path="/communities/:communitySlug/" element={<CommunityPage />} />
            <Route path="/new-construction-homes-under-600k-austin/" element={<NewConstructionUnder600k />} />
            <Route path="/incentives-guide/" element={<IncentivesGuideLanding />} />
            <Route path="/quick-move-in-homes-austin/" element={<QuickMoveInHomes />} />
            <Route path="/new-construction-inspection-checklist/" element={<NewConstructionInspectionChecklist />} />
            <Route path="/move-up-buyers-austin/" element={<MoveUpBuyersAustin />} />
            <Route path="/relocating-to-austin-new-construction/" element={<RelocatingToAustinNewConstruction />} />
            <Route path="/oak-hill-parkway-dripping-springs-impact/" element={<OakHillParkwayDrippingSprings />} />
            <Route path="/build-in-austin/" element={<BuildInAustin />} />
            <Route path="/build-on-your-lot-austin/" element={<BuildOnYourLotAustin />} />
            <Route path="/austin-home-initiative/" element={<AustinHomeInitiative />} />
            <Route path="/austin-custom-home-builders/" element={<AustinCustomHomeBuilders />} />
            <Route path="/property-potential-review/" element={<PropertyPotentialReview />} />
            <Route path="/austin-infill-redevelopment/" element={<AustinInfillRedevelopment />} />
            <Route path="/remodel-or-rebuild-austin/" element={<RemodelOrRebuildAustin />} />
            <Route path="/sell-your-lot-to-a-builder-austin/" element={<SellYourLotToABuilder />} />
            <Route path="/thank-you/" element={<ThankYouPage />} />
            <Route path="/thank-you-cheat-sheet/" element={<ThankYouPage offer="cheat_sheet" />} />
            <Route path="/thank-you-incentives-guide/" element={<ThankYouPage offer="incentives_guide" />} />
            <Route path="/thank-you-builder-rebate/" element={<ThankYouPage offer="builder_rebate" />} />
            <Route path="/thank-you-current-incentives/" element={<ThankYouPage offer="current_incentives" />} />
            <Route path="/thank-you-property-review/" element={<ThankYouPage offer="property_review" />} />
            <Route path="/thank-you-home-valuation/" element={<ThankYouPage offer="home_valuation" />} />
            <Route path="/thank-you-consultation/" element={<ThankYouPage offer="consultation" />} />
            <Route path="/thank-you-inspection-checklist/" element={<ThankYouPage offer="inspection_checklist" />} />
            <Route path="/covercue-support/" element={<CoverCueSupport />} />
            <Route path="/covercue-privacy/" element={<CoverCuePrivacy />} />
            <Route path="/signroute-terms/" element={<SignRouteTerms />} />
            <Route path="/signroute-privacy/" element={<SignRoutePrivacy />} />
            <Route path="/signroute-support/" element={<SignRouteSupport />} />

            <Route path="/index.html" element={<LegacyRedirect to="/" />} />
            <Route path="/home/" element={<LegacyRedirect to="/" />} />
            <Route path="/buying-homes-for-short-term-rentals-vs-converting-your-existing-home-whats-best-for-you/" element={<LegacyRedirect to="/new-construction/" />} />
            <Route path="/house-hunting-checklist/" element={<LegacyRedirect to="/guides/" />} />
            <Route path="/search/*" element={<LegacyRedirect to="/" />} />
            <Route path="/how-to-choose-the-right-real-estate-agent/" element={<LegacyRedirect to="/about/" />} />
            <Route path="/navigating-high-interest-rates-the-rise-of-rate-buy-downs-in-real-estate/" element={<LegacyRedirect to="/guides/builder-financing/" />} />
            <Route path="/mastering-the-austin-real-estate-market-insights-from-a-certified-listing-expert/" element={<LegacyRedirect to="/blog/" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <ChatLauncher />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
